import { CoreEnums } from '@/utils/domain/constants';

const { RpaAgentSessionStatus, RpaAgentEffectiveStatus, RpaAgentSessionEndReason } = CoreEnums;

/**
 * Normalize omitted defaults on session/installation DTOs (PascalCase wire).
 */
export function normalizeInstallation(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    Id: raw.Id ?? null,
    HardwareIdentifier: raw.HardwareIdentifier ?? null,
    DeviceName: raw.DeviceName ?? null,
    FirstSeenAt: raw.FirstSeenAt ?? null,
    LastSeenAt: raw.LastSeenAt ?? null,
    LastIp: raw.LastIp ?? null,
    IsActive: raw.IsActive ?? false,
  };
}

export function normalizeSession(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    Id: raw.Id ?? null,
    RpaAgentId: raw.RpaAgentId ?? null,
    RpaAgentInstallationId: raw.RpaAgentInstallationId ?? null,
    SessionSequence: raw.SessionSequence ?? 0,
    Status: raw.Status ?? RpaAgentSessionStatus.Created,
    StartedAt: raw.StartedAt ?? null,
    EndedAt: raw.EndedAt ?? null,
    EndReason: raw.EndReason ?? null,
    CorrelationId: raw.CorrelationId ?? null,
    Installation: normalizeInstallation(raw.Installation),
  };
}

export function normalizeSessions(list) {
  if (!Array.isArray(list)) return [];
  return list.map(normalizeSession).filter(Boolean);
}

export function isCurrentSession(session) {
  return Boolean(session && !session.EndedAt);
}

/**
 * Effective presence for UX badges (doc §7).
 * Precedence: disabled → unlinked → created | online | offline.
 */
export function getEffectiveAgentStatus(agent, current) {
  if (!(agent?.isActive ?? false)) return RpaAgentEffectiveStatus.Disabled;
  if (!isCurrentSession(current)) return RpaAgentEffectiveStatus.Unlinked;

  switch (current.Status ?? RpaAgentSessionStatus.Created) {
    case RpaAgentSessionStatus.Online:
      return RpaAgentEffectiveStatus.Online;
    case RpaAgentSessionStatus.Offline:
      return RpaAgentEffectiveStatus.Offline;
    default:
      return RpaAgentEffectiveStatus.Created;
  }
}

export function effectiveStatusLocaleKey(status) {
  if (!status) return null;
  return `agents.rpa.status.${status}`;
}

/** Session wire Status → locale for history (includes terminal states). */
export function sessionStatusLocaleKey(status) {
  const value = status ?? RpaAgentSessionStatus.Created;
  const map = {
    [RpaAgentSessionStatus.Created]: 'agents.rpa.sessionStatus.created',
    [RpaAgentSessionStatus.Online]: 'agents.rpa.sessionStatus.online',
    [RpaAgentSessionStatus.Offline]: 'agents.rpa.sessionStatus.offline',
    [RpaAgentSessionStatus.Replaced]: 'agents.rpa.sessionStatus.replaced',
    [RpaAgentSessionStatus.Revoked]: 'agents.rpa.sessionStatus.revoked',
  };
  return map[value] || 'agents.rpa.sessionStatus.created';
}

export function sessionEndReasonLocaleKey(reason) {
  if (reason == null) return null;
  const map = {
    [RpaAgentSessionEndReason.ReplacedByAnotherInstallation]:
      'agents.rpa.sessionEndReason.replacedbyanotherinstallation',
    [RpaAgentSessionEndReason.InstallationReassigned]:
      'agents.rpa.sessionEndReason.installationreassigned',
    [RpaAgentSessionEndReason.ManuallyRevoked]: 'agents.rpa.sessionEndReason.manuallyrevoked',
    [RpaAgentSessionEndReason.AgentDisabled]: 'agents.rpa.sessionEndReason.agentdisabled',
  };
  return map[reason] || null;
}

export { RpaAgentSessionStatus, RpaAgentEffectiveStatus, RpaAgentSessionEndReason };
